import { z } from "zod";
import { AcquisitionActionTypeSchema, AcquisitionFieldRequestSchema } from "./evidence-acquisition";

/**
 * `ExecutionRecipe`：**"在这个站点上这件事该怎么做"的可复用先验**（方案 §4.2 L2）。
 *
 * 它存在的唯一理由是让"新平台不用改代码"：链路描述的是**语义步骤**
 * （"把地区切到目标国家"），而不是选择器或脚本。平台改版只会让模型在实时观察里
 * 重新找控件，不会让这条数据失效 —— 这正是它与"逐平台适配代码"的根本区别。
 *
 * 三条硬边界，都由 `.strict()` 兜住（任何未声明的键直接失败）：
 * 1. **不得携带结构化选择器/脚本**（`selector`/`css`/`xpath`/`script`/`code` 都会让解析失败）；
 * 2. 链路**不含执行权限**：拿到一条链路不能换取任何动作许可；
 * 3. 链路线索（`web_research`）必须带来源，且只能当线索，不得当政策证据（方案 §3 R13）。
 *
 * 边界之外的诚实说明：`.strict()` 管得住**结构化字段**，管不住有人把选择器写成
 * `intent` 里的一句话。那部分靠验收判据 3 的 diff/grep 检查，不靠 schema。
 */

/** 连续失败达到这个次数，链路判为 stale（与取数的 `maxRetriesPerTarget` 是不同概念，不要混用）。 */
export const RECIPE_MAX_CONSECUTIVE_FAILURES = 2;

export const RecipeStepSchema = z.object({
  order: z.number().int().min(1),
  /** 语义意图，如"把地区切到目标国家"。**不是**命令，也不含选择器。 */
  intent: z.string().min(1).max(300),
  /** 期望观察到的语义特征，如 ["地区选择器","显示国家名"]。用于让模型在实时观察里找控件。 */
  expect: z.array(z.string().max(120)).max(6).default([]),
  /** 可选提醒（必须是既有动作白名单里的类型），不是命令。 */
  actionHint: AcquisitionActionTypeSchema.optional(),
  /** 怎么算这一步成功；同样用语义描述。 */
  successCheck: z.string().max(200),
}).strict();
export type RecipeStep = z.infer<typeof RecipeStepSchema>;

export const RecipeOutcomeStatsSchema = z.object({
  attempts: z.number().int().nonnegative().default(0),
  succeeded: z.number().int().nonnegative().default(0),
  failed: z.number().int().nonnegative().default(0),
  /**
   * 连续失败次数（成功即清零）。
   * 方案原文只说"连续失败 2 次 → stale"，但只有 attempts/succeeded/failed 三个
   * **累计值**是算不出"连续"的 —— 所以这个字段是让那条规则真的可计算所必需的。
   */
  consecutiveFailures: z.number().int().nonnegative().default(0),
  lastFailureCode: z.string().max(80).optional(),
}).strict();
export type RecipeOutcomeStats = z.infer<typeof RecipeOutcomeStatsSchema>;

/** 线索级来源。生产链路的提议器也要按同一形状产出，所以导出（单一所有者）。 */
export const RecipeSourceSchema = z.object({
  title: z.string().max(200),
  url: z.string().url().max(2048),
}).strict();

export const ExecutionRecipeSchema = z.object({
  recipeId: z.string().uuid(),
  /** 域名（不是 registry 的 platform id）：链路属于站点，不属于注册表条目。 */
  siteKey: z.string().min(1).max(240),
  /** 任务种类，如 market_demand_search_interest / competitor_scan / review_harvest。 */
  taskKind: z.string().min(1).max(80),
  goalSignature: z.string().min(1).max(300),
  steps: z.array(RecipeStepSchema).min(1).max(20),
  deliverableFields: z.array(AcquisitionFieldRequestSchema).max(40),
  completionCriteria: z.array(z.string().max(240)).min(1).max(12),
  source: z.enum(["model_prior", "web_research", "observed_success", "user_configured"]),
  /** 只在线索级来源下使用；**不得**被当作政策/合规证据（见方案 §3 R13）。 */
  sources: z.array(RecipeSourceSchema).max(8).default([]),
  status: z.enum(["draft", "confirmed", "stale"]),
  recipeVersion: z.number().int().positive(),
  outcomeStats: RecipeOutcomeStatsSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  validatedAt: z.string().optional(),
}).strict().superRefine((value, context) => {
  // 联网找到的链路必须能回溯到来源，否则它是不可核对的传言。
  if (value.source === "web_research" && value.sources.length === 0) {
    context.addIssue({ code: "custom", path: ["sources"], message: "联网得到的链路必须给出来源，不能是不可核对的传言" });
  }
  // 步骤序号必须从 1 开始且不重复：链路是要按序执行的先验。
  const orders = value.steps.map((step) => step.order);
  if (new Set(orders).size !== orders.length) {
    context.addIssue({ code: "custom", path: ["steps"], message: "链路步骤的序号不能重复" });
  }
  if (value.source === "user_configured" && orders.some((order, index) => order !== index + 1)) {
    context.addIssue({ code: "custom", path: ["steps"], message: "用户指定的链路必须按 1 开始的连续顺序保存，不能重排步骤" });
  }
});
export type ExecutionRecipe = z.infer<typeof ExecutionRecipeSchema>;

/**
 * 这条链路现在还能不能复用。两个来源互不覆盖，各管一件事：
 * - `consecutiveFailures >= 上限`：**自动**失效条件，由数据算出，成功即自动恢复；
 * - `status === "stale"`：**显式**失效（人工判定或平台改版信号），不会被一次成功抹掉。
 *
 * 刻意**不**在 `recordRecipeOutcome` 里顺手把 status 改成 stale：
 * 那等于同一个事实写两处，成功清零连续计数后 status 还留着 stale，
 * 链路会永远复用不了（这个 bug 就是被 staleness 用例抓出来的）。
 */
export function isRecipeStale(recipe: ExecutionRecipe): boolean {
  return recipe.status === "stale" || recipe.outcomeStats.consecutiveFailures >= RECIPE_MAX_CONSECUTIVE_FAILURES;
}

/**
 * 记一次执行结果。成功**必须清零连续失败**，否则一次旧故障会永久污染这条链路
 * （累计的 `failed` 保留，用于长期质量判断）。
 */
export function recordRecipeOutcome(
  recipe: ExecutionRecipe,
  outcome: { ok: boolean; failureCode?: string },
  at: string,
): ExecutionRecipe {
  const stats = recipe.outcomeStats;
  const next: RecipeOutcomeStats = outcome.ok
    ? { ...stats, attempts: stats.attempts + 1, succeeded: stats.succeeded + 1, consecutiveFailures: 0, lastFailureCode: undefined }
    : {
      ...stats,
      attempts: stats.attempts + 1,
      failed: stats.failed + 1,
      consecutiveFailures: stats.consecutiveFailures + 1,
      ...(outcome.failureCode ? { lastFailureCode: outcome.failureCode } : {}),
    };
  return ExecutionRecipeSchema.parse({
    ...recipe,
    outcomeStats: next,
    updatedAt: at,
  });
}

/** 链路复用键：站点 + 任务种类 + 目标口径三者都一致才算同一条链路。 */
export function recipeKeyOf(input: { siteKey: string; taskKind: string; goalSignature: string }): string {
  return [input.siteKey.trim().toLowerCase(), input.taskKind.trim().toLowerCase(), input.goalSignature.trim()].join("\u0000");
}

let recipeJsonSchema: unknown;

/**
 * 链路先验的 JSON Schema：与取数方案/证据包一样**只在契约层生成**。
 *
 * 让提示词自己写一份 Schema，就会出现"契约改了、提示词里那份没改"的漂移；
 * 而模型拿到的必须是当前契约。惰性生成，浏览器侧调用不到就不付这份开销。
 */
export function executionRecipeJsonSchema(): unknown {
  return (recipeJsonSchema ??= z.toJSONSchema(ExecutionRecipeSchema));
}

/**
 * 链路**正文**的形状（提议器要产出的那一部分）。
 *
 * 为什么单独有它：提示词里内嵌的 Schema **必须与解析时强制的 Schema 一致**。
 * 先前提示词嵌的是**整条 `ExecutionRecipe`**（含 recipeId/status/recipeVersion/outcomeStats…），
 * 而解析只收 `{steps, sources}` 且 `.strict()`——模型照着给的那份 Schema 填全字段，
 * 结果必被拒。真机一次调用就把这个矛盾暴露了出来。
 * 身份、状态、版本与计数由服务层生成，模型无权决定，所以它们**不出现**在这份 Schema 里。
 */
export const RecipeProposalBodySchema = z.object({
  steps: z.array(RecipeStepSchema).min(1).max(20),
  sources: z.array(RecipeSourceSchema).max(8).optional(),
}).strict();
export type RecipeProposalBody = z.infer<typeof RecipeProposalBodySchema>;

let recipeProposalJsonSchema: unknown;

/** 与取数方案/证据包同一口径：Schema 只在契约层生成，提示词不自己抄一份。 */
export function recipeProposalBodyJsonSchema(): unknown {
  return (recipeProposalJsonSchema ??= z.toJSONSchema(RecipeProposalBodySchema));
}
